import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Card,
  FloatingSave,
  Input,
  List,
  Page,
  SimpleSelect,
  SwitchInput,
} from 'corso-admin-ui';
import { isTruthy, Pegasus } from 'corso-types';
import { FormEventHandler, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
} from '~/hooks/useConfigSettings';
import {
  useThemeSettings,
  useThemeSettingsUpdate,
} from '~/hooks/useThemeSettings';
import { OrderEditingFormSchema } from '~/types';
import { windowMinuteOptions } from './orderEditingOptions';
import OrderEditingRules from './OrderEditingRules';
import OrderEditingWindowOverrides from './OrderEditingWindowOverrides';

export default function OrderEditingSettingsPage() {
  const formId = 'order-editing-settings';
  const formRef = useRef<HTMLFormElement>(null);
  const { data: orderEditingSettings } = useConfigSettings(
    ({ orderEditing }) => orderEditing,
  );

  const { data: themeSettings } = useThemeSettings();

  const { mutateAsync: saveOrderEditingSettings } = useConfigSettingsUpdate();
  const { mutateAsync: saveThemeSettings } = useThemeSettingsUpdate();

  const formMethods = useForm({
    resolver: zodResolver(OrderEditingFormSchema),
    defaultValues: {
      orderEditingWindowMinutes: 30,
      orderEditingWindowOverrides: [],
      isOrderEditingEnabled: false,
      shouldHoldOrdersWithinEditWindow: false,
      orderHoldReleaseTag: undefined,
      shouldUseCreditRefundForOrderEdits: false,
      isRevertUnpaidEditsEnabled: false,
    },
    values:
      orderEditingSettings && themeSettings
        ? {
            orderEditingWindowMinutes:
              orderEditingSettings.orderEditingWindowMinutes,
            orderEditingWindowOverrides:
              orderEditingSettings.orderEditingWindowOverrides,
            isOrderEditingEnabled: orderEditingSettings.isOrderEditingEnabled,
            shouldHoldOrdersWithinEditWindow:
              orderEditingSettings.shouldHoldOrdersWithinEditWindow,
            orderHoldReleaseTag: orderEditingSettings.orderHoldReleaseTag,
            shouldUseCreditRefundForOrderEdits:
              orderEditingSettings.shouldUseCreditRefundForOrderEdits,
            isRevertUnpaidEditsEnabled:
              orderEditingSettings.isRevertUnpaidEditsEnabled,
            theme: {
              orderEditDescriptionText:
                themeSettings.orderEditDescriptionText ??
                'Request changes to your order, such as cancellation or address updates.',
              orderEditExpiredDescriptionText:
                themeSettings.orderEditExpiredDescriptionText ??
                'Order editing is no longer available.',
            },
          }
        : undefined,
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    setValue,
    reset,
    watch,
    register,
  } = formMethods;

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ theme: themeChanges, ...settings }) =>
      // ! no reset() here; a bare reset restores the pre-save server snapshot, so
      // ! saved overrides vanish until the refetch lands. `values` clears dirty for us.
      Promise.all([
        saveOrderEditingSettings({ orderEditing: settings }),
        saveThemeSettings(themeChanges),
      ]),
    )(event).catch(console.error);
  };

  const isOrderEditingEnabled = watch('isOrderEditingEnabled');

  return (
    <FormProvider {...formMethods}>
      <Page
        title="Order Editing"
        secondaryActions={[
          isOrderEditingEnabled && {
            id: 'disable-returns',
            content: 'Turn Off',
            onAction: () => {
              setValue('isOrderEditingEnabled', false, {
                shouldDirty: true,
              });
              formRef.current?.requestSubmit();
            },
          },
        ].filter(isTruthy)}
        primaryAction={
          isOrderEditingEnabled
            ? undefined
            : {
                onAction: () => {
                  setValue('isOrderEditingEnabled', true, {
                    shouldDirty: true,
                  });
                  formRef.current?.requestSubmit();
                },
                content: 'Turn On',
              }
        }
      >
        <form
          id={formId}
          ref={formRef}
          className="flex flex-col gap-5"
          onSubmit={submitHandler}
          onReset={(e) => {
            e.preventDefault();
            reset();
          }}
        >
          {!isOrderEditingEnabled && (
            <Alert
              elevated
              variant="info"
              heading="Order Editing is Disabled. Settings can be configured, but order editing will not be offered to customers."
            />
          )}
          <FloatingSave
            isDirty={isDirty}
            isSubmitting={isSubmitting}
            form={formId}
          />

          <Card>
            <Card.Heading>Settings</Card.Heading>

            <Input
              required
              id="order-edit-description-text"
              label="Description"
              details="The description shown to customers when they can edit their order."
              {...register('theme.orderEditDescriptionText')}
            />

            <Input
              required
              id="order-edit-expired-description-text"
              label="Expired Description"
              details="The description shown when the order editing window has expired, or the order is no longer editable."
              {...register('theme.orderEditExpiredDescriptionText')}
            />

            <Controller
              control={control}
              name="shouldUseCreditRefundForOrderEdits"
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="should-use-credit-refund-for-order-edits"
                  label="Issue Refunds as Store Credit"
                  details="When an order edit results in a refund, credit the customer's store credit account instead of refunding to their original payment method."
                  checked={value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="shouldHoldOrdersWithinEditWindow"
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="should-hold-orders-within-edit-window"
                  label="Enable Order Fulfillment Hold"
                  details="Apply a fulfillment hold on orders that are within the edit window."
                  checked={value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            {watch('shouldHoldOrdersWithinEditWindow') && (
              <>
                <Input
                  id="order-hold-release-tag"
                  label="Hold Tag"
                  details="The tag applied on order creation."
                  {...register('orderHoldReleaseTag')}
                />

                <Controller
                  control={control}
                  name="isRevertUnpaidEditsEnabled"
                  render={({ field: { onChange, value }, fieldState }) => (
                    <SwitchInput
                      id="is-revert-unpaid-edits-enabled"
                      label="Revert Unpaid Edits"
                      details="When the edit window closes, automatically undo any total-increasing edits (added items, upgraded shipping) on orders that the customer did not pay. The order returns to its pre-edit state before fulfillment is released."
                      checked={value}
                      onChange={onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />

                <Alert heading="How It Works" variant="info">
                  <List
                    as="ol"
                    columns={1}
                    items={[
                      `Every new order picks up the ${watch('orderHoldReleaseTag') || Pegasus.OrderEditHoldTag} tag the moment it's placed.`,
                      'Your warehouse uses a rule that pauses or delays fulfillment whenever this tag is present.',
                      'We remove the tag when the editing window expires, even if the customer has already committed their edits.',
                      'Without the tag, the order moves through fulfillment like any other.',
                    ]}
                  >
                    {(step) => (
                      <List.NumberedItem key={step}>{step}</List.NumberedItem>
                    )}
                  </List>
                </Alert>
              </>
            )}
          </Card>

          <Alert heading="Did you know?" variant="tip" elevated>
            95% of all order edits happen within the first 30 minutes of an
            order.
          </Alert>
          <Card>
            <Card.Heading>Edit Window</Card.Heading>

            <Controller
              control={control}
              name="orderEditingWindowMinutes"
              render={({ field: f, fieldState }) => (
                <SimpleSelect
                  options={windowMinuteOptions}
                  details="The time after an order is placed that customers can edit their order."
                  onChange={(value) => f.onChange(Number(value))}
                  label="Default Window"
                  error={fieldState.error?.message}
                  value={
                    windowMinuteOptions.find(
                      (option) => option.value === `${f.value}`,
                    )?.value
                  }
                />
              )}
            />

            <OrderEditingWindowOverrides
              onCommit={() => formRef.current?.requestSubmit()}
            />
          </Card>

          <Alert elevated variant="warning" heading="External Systems">
            Some warehouses and other external systems may not all respect order
            edits, please test your order editing rules before offering it to
            customers.
          </Alert>

          <OrderEditingRules />
        </form>
      </Page>
    </FormProvider>
  );
}
